import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Globe,
  Save,
  Eye,
  CheckCircle,
  Shield,
  Loader,
  ExternalLink,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import toast from 'react-hot-toast';
import securityApi from '../../api/securityApi';

const CC_CATEGORY_LABELS = {
  CC1: 'Control Environment',
  CC2: 'Communication and Information',
  CC3: 'Risk Assessment',
  CC4: 'Monitoring Activities',
  CC5: 'Control Activities',
  CC6: 'Logical and Physical Access Controls',
  CC7: 'System Operations',
  CC8: 'Change Management',
  CC9: 'Risk Mitigation',
};

const ALL_CATEGORIES = Object.keys(CC_CATEGORY_LABELS);

const TrustCenterPage = () => {
  const { projectId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customSlug, setCustomSlug] = useState('');
  const [isPublished, setIsPublished] = useState(false);
  const [companyDescription, setCompanyDescription] = useState('');
  const [showFrameworkBadges, setShowFrameworkBadges] = useState(true);
  const [categoryVisibility, setCategoryVisibility] = useState(
    Object.fromEntries(ALL_CATEGORIES.map((c) => [c, true]))
  );
  const [stats, setStats] = useState({ total: 0, verified: 0, hidden: 0 });
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, [projectId]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await securityApi.getTrustCenterSettings(projectId);
      setCustomSlug(data.custom_slug || '');
      setIsPublished(data.is_published || false);
      setCompanyDescription(data.company_description || '');
      setShowFrameworkBadges(data.show_framework_badges ?? true);

      // Convert visible_categories list to visibility map
      const visibleSet = new Set(data.visible_categories || []);
      const visibility = {};
      for (const cat of ALL_CATEGORIES) {
        visibility[cat] = visibleSet.size === 0 || visibleSet.has(cat);
      }
      setCategoryVisibility(visibility);

      setStats({
        total: data.total_controls || 0,
        verified: data.verified_controls || 0,
        hidden: data.hidden_controls || 0,
      });
    } catch (err) {
      console.error('Error fetching trust center settings:', err);
      toast.error('Failed to load trust center settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchPreview = async () => {
    try {
      setLoadingPreview(true);
      const data = await securityApi.getTrustCenterPreview(projectId);
      setPreview(data);
    } catch (err) {
      console.error('Error fetching preview:', err);
      toast.error('Failed to load preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSave = async () => {
    if (!customSlug || customSlug.trim() === '') {
      toast.error('Please enter a custom slug');
      return;
    }
    try {
      setSaving(true);
      const visibleCategories = ALL_CATEGORIES.filter((c) => categoryVisibility[c]);
      await securityApi.updateTrustCenterSettings(projectId, {
        custom_slug: customSlug,
        is_published: isPublished,
        company_description: companyDescription,
        show_framework_badges: showFrameworkBadges,
        visible_categories: visibleCategories,
      });
      toast.success('Trust center settings saved successfully');
      fetchPreview();
      fetchSettings();
    } catch (err) {
      console.error('Error saving settings:', err);
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const publicUrl = customSlug
    ? `${window.location.origin}/trust/${customSlug}`
    : '';

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Globe className="w-7 h-7 text-teal-600" />
          <h1 className="text-2xl font-bold text-gray-900">Trust Center</h1>
        </div>
        <p className="text-sm text-gray-500">
          Configure and publish your public trust center to share compliance status with customers.
        </p>
      </div>

      {/* Accuracy Indicator */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-blue-900 font-medium">
              Your trust page shows {stats.verified} of {stats.total} total controls.
            </p>
            <p className="text-sm text-blue-700 mt-1">
              {stats.hidden} controls are hidden because they haven't been verified yet.
            </p>
          </div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column: Settings */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Settings</h2>

            {/* Publish Toggle */}
            <div className="mb-6">
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <span className="text-sm font-medium text-gray-900">Publish Trust Center</span>
                  <p className="text-xs text-gray-500 mt-1">
                    Make your trust center publicly accessible
                  </p>
                </div>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={isPublished}
                    onChange={(e) => setIsPublished(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-teal-600 peer-focus:ring-4 peer-focus:ring-teal-300 transition-colors"></div>
                  <div className="absolute left-[2px] top-[2px] bg-white w-5 h-5 rounded-full transition-transform peer-checked:translate-x-5"></div>
                </div>
              </label>
            </div>

            {/* Custom Slug */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Custom URL Slug
              </label>
              <input
                type="text"
                value={customSlug}
                onChange={(e) =>
                  setCustomSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))
                }
                placeholder="your-company-name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
              />
              {publicUrl && (
                <p className="text-xs text-gray-500 mt-2">
                  Public URL: <span className="font-mono">{publicUrl}</span>
                </p>
              )}
            </div>

            {/* Company Description */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Company Description
              </label>
              <textarea
                value={companyDescription}
                onChange={(e) => setCompanyDescription(e.target.value)}
                placeholder="Brief description of your company..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
              />
            </div>

            {/* Show Framework Badges */}
            <div className="mb-6">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={showFrameworkBadges}
                  onChange={(e) => setShowFrameworkBadges(e.target.checked)}
                  className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                />
                <span className="ml-2 text-sm font-medium text-gray-900">
                  Show Framework Badges
                </span>
              </label>
              <p className="text-xs text-gray-500 mt-1 ml-6">
                Display SOC 2 Type I and other framework badges
              </p>
            </div>

            {/* Category Visibility */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-3">
                Category Visibility
              </h3>
              <div className="space-y-2">
                {ALL_CATEGORIES.map((key) => (
                  <label key={key} className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={categoryVisibility[key]}
                      onChange={() =>
                        setCategoryVisibility((prev) => ({ ...prev, [key]: !prev[key] }))
                      }
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      {key}: {CC_CATEGORY_LABELS[key]}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Save Button */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={handleSave}
                disabled={saving}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-medium transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Settings
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Right Column: Live Preview */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-900">Live Preview</h2>
              </div>
              {preview && isPublished && publicUrl && (
                <a
                  href={publicUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700"
                >
                  View Published
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>

            {!preview ? (
              <div className="text-center py-8">
                <Shield className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-600 mb-4">
                  Save your settings to see a preview
                </p>
                <button
                  onClick={fetchPreview}
                  disabled={loadingPreview}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
                >
                  {loadingPreview ? 'Loading...' : 'Load Preview'}
                </button>
              </div>
            ) : loadingPreview ? (
              <div className="flex items-center justify-center py-8">
                <Loader className="w-6 h-6 text-teal-600 animate-spin" />
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg p-4 max-h-[600px] overflow-y-auto">
                {/* Preview Header */}
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {preview.company_name || 'Your Company'}
                  </h3>
                  {preview.company_description && (
                    <p className="text-sm text-gray-600">{preview.company_description}</p>
                  )}
                  <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                    {preview.show_framework_badges && preview.framework_badges?.map((badge, i) => (
                      <span key={i} className="px-3 py-1 bg-teal-100 text-teal-700 text-xs font-medium rounded-full">
                        {badge.name} — {badge.status}
                      </span>
                    ))}
                    {preview.total_verified > 0 && (
                      <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        {preview.total_verified} verified controls
                      </span>
                    )}
                  </div>
                </div>

                {/* Preview Body */}
                {preview.categories && preview.categories.length > 0 ? (
                  <div className="space-y-4">
                    {preview.categories.map((category) => (
                      <div key={category.category} className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">
                          {category.category_label} ({category.controls.length})
                        </h4>
                        <div className="space-y-2">
                          {category.controls.map((control, idx) => (
                            <div
                              key={idx}
                              className="flex items-start gap-2 text-xs"
                            >
                              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                              <div className="flex-1">
                                <p className="text-gray-900 font-medium">{control.title}</p>
                                <div className="flex items-center gap-1 mt-0.5">
                                  {control.verification_method === 'Continuously monitored' ? (
                                    <RefreshCw className="w-3 h-3 text-blue-500" />
                                  ) : (
                                    <CheckCircle className="w-3 h-3 text-green-500" />
                                  )}
                                  <span className="text-gray-500">{control.verification_method}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <AlertCircle className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">
                      No verified controls available to display
                    </p>
                  </div>
                )}

                {/* Preview Footer */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500 text-center">
                    This trust page only displays security controls that have been independently
                    verified through automated integrations or documented evidence. Controls not
                    shown are either in progress or pending verification.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrustCenterPage;
